import { Link, useParams, Navigate } from 'react-router-dom';
import { ARTICLES, SITE } from '../data/site';
import { Seo } from '../lib/seo';
import { Reveal } from '../components/motion';
import { CTABand, Breadcrumbs } from '../components/chrome';

export default function ArticlePage() {
  const { slug } = useParams();
  const art = ARTICLES.find((a) => a.slug === slug);
  if (!art) return <Navigate to="/insights" replace />;

  const crumbs = [{ label: 'Home', href: '/' }, { label: 'Insights', href: '/insights' }, { label: art.title }];
  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: art.title,
    description: art.excerpt,
    datePublished: art.date,
    author: { '@type': 'Organization', name: SITE.name, url: SITE.url },
    publisher: { '@type': 'Organization', name: SITE.name, url: SITE.url },
    mainEntityOfPage: `${SITE.url}/insights/${art.slug}`,
  };

  const others = ARTICLES.filter((a) => a.slug !== art.slug);

  return (
    <div data-testid={`page-article-${art.slug}`}>
      <Seo
        title={`${art.title} | ToxGlobal Analytics`}
        description={art.excerpt}
        path={`/insights/${art.slug}`}
        ld={[articleLd]}
        breadcrumbs={crumbs}
      />

      <article>
        <header className="border-b border-slate-200 bg-paper">
          <div className="container-x py-14 sm:py-20 max-w-4xl">
            <Breadcrumbs items={crumbs} />
            <Reveal>
              <p className="eyebrow text-signal mb-4">
                {new Date(art.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} · {art.read}
              </p>
              <h1 className="display text-3xl sm:text-5xl text-ink">{art.title}</h1>
              <p className="mt-5 text-lg text-slate-600 leading-relaxed">{art.excerpt}</p>
            </Reveal>
          </div>
        </header>

        <div className="container-x py-12 sm:py-16 max-w-4xl" data-testid="article-body">
          <Reveal>
            <div className="clipped-frame overflow-hidden bg-midnight aspect-[16/8] mb-12">
              <img src={art.image} alt={art.alt} className="h-full w-full object-cover" />
            </div>
          </Reveal>
          {art.sections.map((s, i) => (
            <Reveal key={s.h} delay={0.02}>
              <section className="mb-10">
                <h2 className="display text-2xl sm:text-3xl text-ink mb-4">{s.h}</h2>
                {(s.ps || []).map((p, j) => (
                  <p key={j} className="text-slate-700 leading-relaxed mb-4">{p}</p>
                ))}
                {s.list && (
                  <ul className="border-t border-slate-200 mt-4">
                    {s.list.map((li) => (
                      <li key={li} className="py-3 border-b border-slate-200 text-slate-700 flex gap-3">
                        <span className="font-mono text-signal text-xs mt-1">—</span> {li}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </Reveal>
          ))}

          <Reveal>
            <div className="spotlight-dark grain text-white p-8 sm:p-10 mt-14" data-testid="article-cta">
              <p className="eyebrow text-signal mb-3">Applying this to a live requirement?</p>
              <h2 className="display text-2xl sm:text-3xl">ToxGlobal quotes, installs, and supports what it writes about.</h2>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/contact" className="btn-light" data-testid="article-cta-quote">Request a Quote</Link>
                <a href={`mailto:${SITE.email}`} className="btn-outline-light" data-testid="article-cta-email">Email the team</a>
              </div>
            </div>
          </Reveal>
        </div>
      </article>

      <section className="section-pad bg-white hairline-t" data-testid="article-related">
        <div className="container-x">
          <h2 className="eyebrow text-signal mb-8">Keep reading</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {others.map((o) => (
              <Link key={o.slug} to={`/insights/${o.slug}`} className="group border border-slate-200 hover:border-signal transition-colors" data-testid={`article-related-${o.slug}`}>
                <div className="overflow-hidden aspect-[16/9] bg-midnight">
                  <img src={o.image} alt={o.alt} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <h3 className="font-display font-semibold text-ink group-hover:text-signal transition-colors leading-snug">{o.title}</h3>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">{o.read}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
